import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Sparkles, Smartphone, Calendar } from 'lucide-react';
import DownloadModal from './DownloadModal';

const phrases = ["Suivez.", "Analysez.", "Progressez."];

const screens = [
  { src: '/assets/img/hero-phone-6.jpg', label: 'Sessions' },
  { src: '/assets/img/hero-phone-1.jpg', label: 'Statistiques' },
  { src: '/assets/img/hero-phone-3.jpg', label: 'Stocks' },
  { src: '/assets/img/hero-phone-2.jpg', label: 'Carte' },
  { src: '/assets/img/hero-phone-4.jpg', label: 'Concours' },
  { src: '/assets/img/hero-phone-5.jpg', label: 'Profil' },
];

// Floating stat cards displayed around the phone — synced with active slide.
const FLOATING_STATS = [
  { label: 'Moyenne', value: '62%', accent: '#FF6B35', side: 'left',  top: '14%' },
  { label: 'Ce mois', value: '+8%', accent: '#22C55E', side: 'right', top: '32%' },
  { label: 'Skeet',   value: '4 sessions', accent: '#FFC107', side: 'left',  top: '62%' },
];

export default function Hero() {
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [text, setText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const timeoutRef = useRef(null);

  // Typewriter
  useEffect(() => {
    const phrase = phrases[currentPhrase];
    const typingSpeed = isDeleting ? 40 : 80;
    if (!isDeleting && text === phrase) {
      timeoutRef.current = setTimeout(() => setIsDeleting(true), 1500);
      return;
    }
    if (isDeleting && text === '') {
      setIsDeleting(false);
      setCurrentPhrase((prev) => (prev + 1) % phrases.length);
      return;
    }
    timeoutRef.current = setTimeout(() => {
      setText((prev) =>
        isDeleting ? phrase.substring(0, prev.length - 1) : phrase.substring(0, prev.length + 1),
      );
    }, typingSpeed);
    return () => clearTimeout(timeoutRef.current);
  }, [text, isDeleting, currentPhrase]);

  // Slideshow
  useEffect(() => {
    const id = setInterval(() => setCurrentSlide((p) => (p + 1) % screens.length), 3200);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 lg:pt-20 pb-16"
    >
      {/* Background image + gradient */}
      <div className="absolute inset-0">
        <img src="/assets/img/hero-bg.png" alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/70 via-[#0D0D0D]/85 to-[#0D0D0D]" />
        {/* Radial glow behind phone area */}
        <div className="hidden lg:block absolute right-[5%] top-1/2 -translate-y-1/2 w-[520px] h-[520px] bg-[#FF6B35]/12 rounded-full blur-[140px]" />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="particle bg-[#FF6B35]/20"
            style={{
              width: `${8 + Math.random() * 12}px`,
              height: `${8 + Math.random() * 12}px`,
              left: `${10 + Math.random() * 80}%`,
              animation: `float-up ${10 + Math.random() * 15}s linear infinite`,
              animationDelay: `${Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center">
        {/* ===== LEFT — Content ===== */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="order-2 lg:order-1 text-center lg:text-left"
        >
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-6">
            <Sparkles size={13} className="text-[#FF6B35]" />
            <span className="text-[#FF6B35] text-[11px] font-semibold uppercase tracking-wider">
              Coach IA inclus · Clayo
            </span>
          </div>

          <h1 className="font-['Poppins'] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[1.05] mb-6 min-h-[1.05em]">
            <span className="text-white">{text}</span>
            <span className="inline-block w-[2px] h-[0.9em] align-middle ml-1 bg-[#FF6B35] animate-pulse" />
          </h1>

          <p className="text-[#B5B5B5] text-base sm:text-lg max-w-xl mx-auto lg:mx-0 leading-relaxed mb-8">
            L&apos;application n°1 de suivi de performance pour les tireurs de ball-trap.
            Skeet, Trap, DTL — toutes vos sessions, votre coach IA et la carte des stands dans votre poche.
          </p>

          {/* CTA — single download button opens a platform-choice modal */}
          <div className="flex flex-col sm:flex-row gap-3 mb-10 justify-center lg:justify-start">
            <button
              type="button"
              onClick={() => setDownloadOpen(true)}
              data-testid="hero-cta-download"
              className="pulse-cta inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold text-base hover:shadow-[0_0_30px_rgba(255,107,53,0.4)] transition-all duration-300 hover:scale-105"
            >
              <Download size={20} />
              Télécharger ClayPulse
            </button>
          </div>

          {/* Trust strip */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-md mx-auto lg:mx-0">
            {[
              { icon: Sparkles,  value: '8', label: 'disciplines' },
              { icon: Calendar,  value: '7j', label: 'essai gratuit' },
              { icon: Smartphone, value: 'iOS/Android', label: 'disponible' },
            ].map((badge, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + i * 0.12, duration: 0.5 }}
                className="flex flex-col items-center lg:items-start gap-1 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/5"
              >
                <div className="flex items-center gap-1.5 text-[#FF6B35]">
                  <badge.icon size={13} />
                  <span className="font-bold text-sm sm:text-base font-['Poppins'] whitespace-nowrap">{badge.value}</span>
                </div>
                <span className="text-[#888] text-[10px] sm:text-xs">{badge.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ===== RIGHT — Phone mockup ===== */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
          className="order-1 lg:order-2 flex flex-col justify-center items-center relative"
          data-testid="hero-phone-block"
        >
          {/* Wrapper with perspective for 3D tilt on desktop */}
          <div
            className="relative"
            style={{ perspective: '1400px' }}
          >
            {/* Floating stat cards (desktop only) */}
            {FLOATING_STATS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: s.side === 'left' ? -30 : 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.6 }}
                className={`hidden lg:flex absolute z-20 items-center gap-2 px-3 py-2 rounded-xl bg-[#141414]/95 backdrop-blur-md border border-white/10 shadow-[0_8px_30px_rgba(0,0,0,0.5)]`}
                style={{
                  top: s.top,
                  [s.side]: '-72px',
                  animation: `floatY 4s ease-in-out ${i * 0.4}s infinite`,
                }}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: s.accent }} />
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-wider text-white/50 leading-tight">{s.label}</div>
                  <div className="text-white font-bold text-sm leading-tight">{s.value}</div>
                </div>
              </motion.div>
            ))}

            {/* Phone frame with 3D tilt (desktop) */}
            <motion.div
              whileHover={{ rotateY: 0, rotateX: 0, scale: 1.02 }}
              transition={{ duration: 0.5 }}
              className="relative z-10 w-[230px] sm:w-[260px] lg:w-[300px] rounded-[42px] border-[8px] border-[#222] bg-[#0a0a0a] shadow-[0_30px_80px_rgba(0,0,0,0.7),0_0_60px_rgba(255,107,53,0.1)] overflow-hidden"
              style={{
                transformStyle: 'preserve-3d',
                transform: typeof window !== 'undefined' && window.innerWidth >= 1024 ? 'rotateY(-8deg) rotateX(2deg)' : 'none',
              }}
            >
              {/* Dynamic Island */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[90px] h-[24px] bg-black rounded-full z-30 flex items-center justify-end pr-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]/80" />
              </div>
              {/* Status bar */}
              <div className="absolute top-2 left-0 right-0 z-20 flex justify-between items-center px-5 text-[10px] font-semibold text-white/80">
                <span>9:41</span>
                <div className="w-[90px]" />
                <div className="flex items-center gap-1">
                  <span className="text-[8px]">●●●●</span>
                </div>
              </div>
              {/* Screen slideshow */}
              <div className="relative aspect-[9/19] bg-black overflow-hidden" data-testid="hero-phone-mockup">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSlide}
                    src={screens[currentSlide].src}
                    alt={`ClayPulse — ${screens[currentSlide].label}`}
                    initial={{ opacity: 0, x: 40, scale: 1.02 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -40, scale: 0.98 }}
                    transition={{ duration: 0.45, ease: 'easeInOut' }}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                </AnimatePresence>
              </div>
              {/* Home indicator */}
              <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-[100px] h-[4px] bg-white/25 rounded-full z-20" />
            </motion.div>
          </div>

          {/* Screen label + dot navigation */}
          <div className="flex flex-col items-center mt-6 relative z-10">
            <AnimatePresence mode="wait">
              <motion.span
                key={currentSlide}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                transition={{ duration: 0.25 }}
                className="text-[#FF6B35] text-xs font-semibold uppercase tracking-[0.2em] mb-2"
                data-testid="hero-screen-label"
              >
                {screens[currentSlide].label}
              </motion.span>
            </AnimatePresence>
            <div className="flex gap-1.5">
              {screens.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  data-testid={`hero-slide-dot-${i}`}
                  aria-label={`Voir ${screens[i].label}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === currentSlide ? 'w-6 bg-[#FF6B35]' : 'w-1.5 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* === Download choice modal === */}
      <DownloadModal open={downloadOpen} onClose={() => setDownloadOpen(false)} />

      {/* Local keyframes (scoped via global stylesheet) */}
      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </section>
  );
}
